/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import { Actionable, Text, View } from '@az/starc-ui';
import { useMemo, useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLabels } from '@/hooks/useLabels';

type ContentProps = {
  text: string;
  detailsModal?: boolean | undefined;
  truncate?: boolean;
};

const labelMap = {
  contentAnnounced: 'label_ada_contentAnnounced',
  seeMore: 'label_reviews_seeMore',
  seeLess: 'label_reviews_seeLess',
};

export const Content = ({ text, detailsModal, truncate = true }: ContentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const labels = useLabels(labelMap);

  const { isLongText } = useMemo(() => {
    if (!truncate) return { isLongText: false };
    const maxLength = isMobile ? 415 : 680;
    return { isLongText: text.length > maxLength };
  }, [text, truncate, isMobile]);

  const moreOrLessTextLabel = isOpen ? labels.seeLess : labels.seeMore;
  if (!detailsModal) {
    return (
      <View className={styles.textContainer}>
        <Text as="p" variant="main-body" className={isLongText && !isOpen && styles.longText}>
          {text}
        </Text>
        {isLongText && (
          <Actionable
            attributes={{
              'aria-disabled': true,
              'aria-label': `${labels.contentAnnounced} (${moreOrLessTextLabel})`,
            }}
            className={styles.seeLessButton}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <Text variant="inline-link" decoration="underline">
              {moreOrLessTextLabel}
            </Text>
          </Actionable>
        )}
      </View>
    );
  }
  return (
    <View className={styles.modalTextContainer}>
      <Text as="p" variant="main-body">
        {text}
      </Text>
    </View>
  );
};
